import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];
  
  // FIX: Declare the private field so the script doesn't crash!
  #previousScrollY = 0;

  connectedCallback() {
    console.log(`[DialogComponent] 🟢 connectedCallback fired for ID: ${this.id}, Tag: ${this.tagName}`);
    
    super.connectedCallback();

    if (!this.refs.dialog) {
      console.error(`[DialogComponent] ❌ ERROR: Required ref="dialog" NOT FOUND inside ${this.tagName}. The script will fail.`);
    } else {
      console.log(`[DialogComponent] ✅ SUCCESS: ref="dialog" element found inside ${this.tagName}.`);
    }

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    console.log(`[DialogComponent] 🔴 disconnectedCallback fired for ID: ${this.id}`);
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      console.log(`[DialogComponent] Resize threshold hit. Auto-closing.`);
      this.closeDialog();
    }
  }, 50);

  /**
   * Shows the dialog.
   */
  showDialog() {
    console.log(`[DialogComponent] ⚡ showDialog() triggered on ID: ${this.id}`);
    
    const { dialog } = this.refs;

    if (!dialog) {
      console.error(`[DialogComponent] Cannot open. The inner <dialog ref="dialog"> tag is missing.`);
      return;
    }

    if (dialog.open) {
      console.log(`[DialogComponent] Dialog is already open. Ignoring request.`);
      return;
    }

    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      console.log(`[DialogComponent] Firing native showModal() (Scroll lock disabled)`);
      
      /* 
       * DISABLED: Aggressive body locking that breaks sticky headers and causes layout shifts 
       *
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      */

      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    console.log(`[DialogComponent] 🛑 closeDialog() triggered on ID: ${this.id}`);
    const { dialog } = this.refs;

    if (!dialog || !dialog.open) {
      console.log(`[DialogComponent] Dialog is already closed or missing. Ignoring close request.`);
      return;
    }

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // Force browser to restart animation by resetting it
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    console.log(`[DialogComponent] Restoring position (Scroll lock disabled).`);
    
    /* 
     * DISABLED: Aggressive body unlocking that causes page jumps 
     *
    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
    */

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    console.log(`[DialogComponent] 🔄 toggleDialog() triggered`);
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      console.log(`[DialogComponent] Clicked outside modal detected. Auto-closing.`);
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    console.log(`[DialogComponent] Escape key pressed. Auto-closing.`);
    event.preventDefault();
    this.closeDialog();
  }

  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          /* 
           * DISABLED: Secondary scroll lock mechanism
           * document.documentElement.setAttribute('scroll-lock', '');
           */
        } else {
          /*
           * DISABLED: Secondary scroll unlock mechanism
           * document.documentElement.removeAttribute('scroll-lock');
           */
        }
      }
    }
  },
  { capture: true }
);